// ── Board filters component ──

import { escapeHtml, debounce, renderAvatarHtml, TYPES, TYPE_COLORS, TYPE_LABELS, getTypeIcon } from '../utils.js';
import { statusNames, statusLabel } from '../lib/statuses.js';
import { URGENCY_VALUES, urgencyCopy, urgencyLabel } from '../lib/task-urgency.js';
import { groupContainerTitle } from '../lib/task-group-label.js';

// Workspace scope (TPT447) adds a `projects` id array on top of this shape; the board never has
// one, so it is deliberately not a default here (every reader falls back with `|| []`).
export const FILTER_DEFAULTS = { q: '', types: [], statuses: [], assignees: [], tags: [], sprints: [], urgencies: [] };

// URL param names filtersToParams()/filtersFromParams() read/write — kept alongside them
// (single source of truth) so hasFilterParams() below never drifts from the real param set.
const FILTER_PARAM_KEYS = ['q', 'type', 'status', 'assignee', 'tag', 'sprint', 'project', 'urgency'];

// API caps on the two free-text dimensions (task-list-filters.js MAX_Q_LENGTH/MAX_TAG_LENGTH;
// the workspace search caps `q` at 200 too). The inputs carry them as maxlength: a longer value
// would make the board's server-side fetch answer 400 instead of just matching nothing.
const MAX_SEARCH_LENGTH = 200;
const MAX_TAG_LENGTH = 100;

const isWorkspace = (opts) => !!opts && opts.scope === 'workspace';

// Multi-select assignee toggle (C1405). Pure + exported so multi-select/Unassigned rule
// is unit-testable w/o DOM — same discipline as api-backend.js's filterToOwnOrUnassigned
// (C1407, Task App board scoping). uid 0 = Unassigned pseudo-member, see renderBoardFilters
// below. Never mutate `assignees` in place — board.js's state.filters starts as a shallow
// spread of FILTER_DEFAULTS (`{ ...FILTER_DEFAULTS }`), so filters.assignees is initially
// the SAME array object as FILTER_DEFAULTS.assignees; a push() here would permanently
// poison the module-level default for every future board mount.
export function toggleAssignee(assignees, uid) {
  return assignees.includes(uid) ? assignees.filter(a => a !== uid) : [...assignees, uid];
}

// Trigger-button avatar-stack math (TPT110). Pure + exported so the "cap at `max`,
// overflow into a +N badge" rule is unit-testable w/o DOM — same discipline as
// toggleAssignee above. `uid === 0` (Unassigned) and an unknown/stale uid (a member
// removed from the project since the filter was set) both resolve to `member: null` —
// the caller renders the Unassigned glyph for uid 0 and initials-from-nothing would be
// wrong for a genuinely unknown id, so callers must branch on `uid` first, `member`
// second. Order follows `assignees` as given (selection order), not member-list order.
export function assigneeStackEntries(assignees, members, max = 5) {
  const list = assignees || [];
  const shown = list.slice(0, max).map(uid => ({
    uid,
    member: uid === 0 ? null : (members || []).find(m => m.user_id === uid) || null,
  }));
  return { shown, overflow: Math.max(0, list.length - max) };
}

// ── Pure filter function ──
// The reference semantics of every dimension. The board no longer runs it (TPT597): its rows
// come from GET /tasks filtered server-side (filtersToQuery() below), and
// api/src/lib/task-list-filters.js mirrors these rules. computeStatusCounts() still uses it for
// a caller that holds the whole task list.

export function applyFilters(tasks, filters) {
  let result = tasks;

  if (filters.q) {
    const q = filters.q.toLowerCase();
    result = result.filter(t =>
      t.title.toLowerCase().includes(q) ||
      (t.description || '').toLowerCase().includes(q) ||
      t.task_key.toLowerCase().includes(q)
    );
  }

  if (filters.types.length > 0) {
    result = result.filter(t => filters.types.includes(t.type || 'task'));
  }

  if (filters.statuses.length > 0) {
    result = result.filter(t => filters.statuses.includes(t.status));
  }

  if (filters.assignees.length > 0) {
    result = result.filter(t => {
      if (!t.assignee) return filters.assignees.includes(0);
      return filters.assignees.includes(t.assignee);
    });
  }

  if (filters.tags.length > 0) {
    result = result.filter(t =>
      filters.tags.every(tag => (t.tags || []).includes(tag))
    );
  }

  if ((filters.urgencies || []).length > 0) {
    result = result.filter(t => filters.urgencies.includes(t.urgency ?? 0));
  }

  if (filters.sprints.length > 0) {
    result = result.filter(t => filters.sprints.includes(t.priority));
  }

  return result;
}

// ── Status filter selection math (TPT107) ──
// Pure, DOM-free helpers for the status-filter dropdown's checked-state and toggle logic —
// ported from the Task App's status-filter-select.js (C1547), same rules, array instead of
// Set (filters.statuses is a plain array here, URL-serialized by filtersToParams()). Empty
// array means "show all", same convention as every other dimension in this file.

// Whether one row's checkbox should render checked. value === '' is the "All statuses" row
// (rendered by the TPT108 dropdown); every other value is a status name.
export function isStatusRowChecked(selected, value) {
  const sel = Array.isArray(selected) ? selected : [];
  if (value === '') return sel.length === 0;
  return sel.length === 0 || sel.includes(value);
}

// Computes the next filters.statuses array after toggling one row. Always returns a NEW
// array — never mutates `selected` (same shallow-spread-aliasing hazard as toggleAssignee
// below: state.filters starts as `{ ...FILTER_DEFAULTS }`, so filters.statuses is initially
// the SAME array object as FILTER_DEFAULTS.statuses until reassigned).
//
// - value === '' (the All-statuses row): always resets to [], regardless of `checked`.
// - checking a row: add it to a copy of the current selection (no duplicate).
// - unchecking a row while the current selection is empty (the "all" state, every row
//   renders checked): a plain .filter() on an empty array would be a silent no-op, so this
//   instead EXPANDS into the explicit complement — every known name except the one just
//   unticked — so the uncheck actually narrows the board.
// - unchecking a row from an explicit subset: remove it from a copy.
// - normalize: if the result ends up covering every known name, collapse back to the
//   canonical empty array so persistence/label/All-row checkbox all agree "all" means [].
export function nextStatusSelection(selected, value, checked, allNames) {
  const names = Array.isArray(allNames) ? allNames : [];
  const current = Array.isArray(selected) ? selected : [];

  if (value === '') return [];

  let next;
  if (checked) {
    next = current.includes(value) ? [...current] : [...current, value];
  } else if (current.length === 0) {
    next = names.filter(n => n !== value);
  } else {
    next = current.filter(n => n !== value);
  }

  if (names.length > 0 && next.length >= names.length && names.every(n => next.includes(n))) {
    return [];
  }
  return next;
}

// Faceted per-status counts — every filter dimension EXCEPT status applies (statuses: []),
// so each row's number answers "how many tasks would I see if I picked only this status".
// Shape: { all: <total after every non-status filter>, <statusName>: <count>, ... } — a
// status with zero matching tasks simply has no key; callers read `counts[name] || 0` (same
// convention the Task App's own DOM-patch count mirror uses). Caveat: a project status
// literally named "all" would collide with the `all` total key.
export function computeStatusCounts(tasks, filters) {
  const base = applyFilters(tasks || [], { ...FILTER_DEFAULTS, ...(filters || {}), statuses: [] });
  const counts = { all: base.length };
  for (const t of base) {
    if (t.status) counts[t.status] = (counts[t.status] || 0) + 1;
  }
  return counts;
}

// ── Sprint filter helpers ──
// Task↔sprint linkage is convention-only: task.priority === sprint.number (no FK).

// Selectable sprint numbers: union of sprint-table numbers + in-use task
// priorities (mirrors sprintOptionsHtml in utils.js), plus 0 (Backlog) when
// any task sits there. Sorted desc (newest sprint first).
export function sprintFilterNumbers(sprints, tasks) {
  const nums = new Set();
  for (const s of (sprints || [])) if (s.number > 0) nums.add(s.number);
  for (const t of (tasks || [])) if (t.priority > 0) nums.add(t.priority);
  const hasBacklog = (tasks || []).some(t => t.priority === 0);
  const sorted = [...nums].sort((a, b) => b - a);
  return hasBacklog ? [...sorted, 0] : sorted;
}

// preset: 'recent' → 5 highest sprint numbers, 'all' → [] (no filtering).
export function applySprintPresets(sprints, preset) {
  if (preset === 'recent') {
    return [...sprints].sort((a, b) => b - a).slice(0, 5);
  }
  if (preset === 'all') {
    return [];
  }
  return sprints;
}

// Chip/dropdown label for a sprint number — "Backlog" for 0, sprint name when
// a matching sprints-table row exists, else the bare number.
export function sprintLabel(sprints, num, singular = 'Sprint') {
  if (num === 0) return 'Backlog';
  const meta = (sprints || []).find(s => s.number === num);
  // (C1395) meta.name is boilerplate ("Sprint N", written by every sprint-creating writer
  // regardless of task_group_label) far more often than not — groupContainerTitle() strips
  // it instead of appending it verbatim (was: "Batch 12 — Sprint 12" for every ordinary row).
  return groupContainerTitle(num, meta, singular);
}

// ── Dropdown open/scroll registry + shared outside-click/Escape handler (C1166, generalized
// TPT109, TPT108 added Escape) ──
//
// Every .ws-dropdown-style filter (sprint, type, assignee, status) needs the same two
// things: an open/scroll flag pair that survives refreshFilterBar()'s innerHTML wipe, and
// exactly one document-level mousedown listener (plus one keydown listener for Escape) that
// closes whichever one is open. One registry + one handler pair for all of them, keyed by
// dimension, so each new dropdown is a new key instead of a new copy of this block. Each
// key's DOM ids are `bf-<key>-btn`/`bf-<key>-dropdown`.
const dropdownState = {
  sprint: { open: false, scroll: 0 },
  type: { open: false, scroll: 0 },
  urgency: { open: false, scroll: 0 },
  assignee: { open: false, scroll: 0 },
  status: { open: false, scroll: 0 },   // TPT108
  project: { open: false, scroll: 0 },  // TPT447 (workspace scope only)
};
let filterOutsideHandler = null;   // mirrors old kanban.js:49 — exactly one document listener, all dropdowns
let filterEscapeHandler = null;    // sibling of the above — Escape closes whichever dropdown is open (TPT108)
// Token of the bindBoardFilters() call that owns the listener pair above (TPT643). Each bind
// returns a fresh one; unbindBoardFilters(token) acts only while that token still owns, so a
// page teardown that runs late cannot strip the filter bar of the page now on screen.
let filterBindingOwner = null;

// tasks = rows whose `priority` marks a sprint in use, project-wide — never the filtered board
// page, or the option list would collapse to the current selection. The board passes
// `{ priority }` rows built from GET /tasks/facets' `priorities` (TPT597).
export function renderSprintSelector(sprints, tasks, selected, opts = {}) {
  const open = opts.open !== undefined ? opts.open : dropdownState.sprint.open;
  const plural = opts.groupLabel || 'Sprints';
  const singular = opts.groupLabelSingular || 'Sprint';
  const numbers = sprintFilterNumbers(sprints, tasks);
  const sel = selected || [];
  const countLabel = sel.length ? `${sel.length} selected` : `All ${plural}`;

  let html = '<div class="bf-sprint-wrap" id="bf-sprint-wrap">';
  html += `<button type="button" class="btn btn-ghost btn-sm bf-sprint-btn" id="bf-sprint-btn" aria-haspopup="true" aria-expanded="${open}">${escapeHtml(plural)}: ${escapeHtml(countLabel)} <span style="font-size:0.7rem">&#9662;</span></button>`;
  html += `<div class="ws-dropdown bf-sprint-dropdown${open ? ' open' : ''}" id="bf-sprint-dropdown">`;
  html += '<div class="bf-sprint-presets">';
  html += `<button type="button" class="btn btn-ghost btn-sm" data-preset="recent">Recent ${escapeHtml(plural)}</button>`;
  html += `<button type="button" class="btn btn-ghost btn-sm" data-preset="all">All ${escapeHtml(plural)}</button>`;
  html += '</div>';
  if (numbers.length === 0) {
    html += `<div class="text-faint" style="padding:0.5rem;font-size:0.8rem;">No ${escapeHtml(plural.toLowerCase())} yet</div>`;
  } else {
    for (const n of numbers) {
      const meta = (sprints || []).find(s => s.number === n);
      const closedClass = meta && meta.status === 'closed' ? ' bf-sprint-option-closed' : '';
      html += `<label class="bf-sprint-option${closedClass}"><input type="checkbox" value="${n}" ${sel.includes(n) ? 'checked' : ''}> ${escapeHtml(sprintLabel(sprints, n, singular))}</label>`;
    }
  }
  html += '</div></div>';
  return html;
}

function detachFilterOutsideHandler() {
  if (filterOutsideHandler) {
    document.removeEventListener('mousedown', filterOutsideHandler);
    filterOutsideHandler = null;
  }
  if (filterEscapeHandler) {
    document.removeEventListener('keydown', filterEscapeHandler);
    filterEscapeHandler = null;
  }
}

// Deferred registration — mirrors assignee-picker.js: the opening click (a mousedown
// that bubbles to document) would otherwise close the dropdown it just opened instantly.
// Registers ONE listener covering every key in dropdownState — opening one dropdown's
// trigger is an "outside click" for any other dropdown already open, so this also closes
// a sibling dropdown for free with no extra wiring.
// `token` is the binding this attach belongs to: a newer bind or an unbind since then wins,
// so a pending timeout never attaches listeners for a binding that is already gone.
function attachFilterOutsideHandler(token) {
  setTimeout(() => {
    if (filterBindingOwner !== token) return;
    detachFilterOutsideHandler();        // absorb a pending timeout from a prior bind
    filterOutsideHandler = (ev) => {
      for (const key of Object.keys(dropdownState)) {
        // Resolve live nodes: refreshFilterBar() replaces them on every change, so
        // nodes closed over at bind time would go stale.
        const liveDd = document.getElementById(`bf-${key}-dropdown`);
        const liveBtn = document.getElementById(`bf-${key}-btn`);
        if (!liveDd) continue;           // dropdown not rendered this pass (e.g. sprints off)
        if (liveDd.contains(ev.target)) continue;
        if (liveBtn && liveBtn.contains(ev.target)) continue;
        dropdownState[key].open = false;
        liveDd.classList.remove('open');
        if (liveBtn) liveBtn.setAttribute('aria-expanded', 'false');
      }
    };
    // Escape closes whichever dropdown is currently open and returns focus to its
    // trigger — same deferred registration + shared-across-every-key shape as the
    // outside-click handler above, detached alongside it so the leak guard covers both.
    filterEscapeHandler = (ev) => {
      if (ev.key !== 'Escape') return;
      for (const key of Object.keys(dropdownState)) {
        if (!dropdownState[key].open) continue;
        const liveDd = document.getElementById(`bf-${key}-dropdown`);
        const liveBtn = document.getElementById(`bf-${key}-btn`);
        dropdownState[key].open = false;
        if (liveDd) liveDd.classList.remove('open');
        if (liveBtn) {
          liveBtn.setAttribute('aria-expanded', 'false');
          liveBtn.focus();
        }
      }
    };
    document.addEventListener('mousedown', filterOutsideHandler);
    document.addEventListener('keydown', filterEscapeHandler);
  }, 0);
}

function bindSprintSelector(filters, onChange, sprints, tasks) {
  const btn = document.getElementById('bf-sprint-btn');
  const dd = document.getElementById('bf-sprint-dropdown');
  if (!btn || !dd) return;

  // Restore scroll position across the innerHTML wipe every filter change causes.
  if (dropdownState.sprint.open) dd.scrollTop = dropdownState.sprint.scroll;
  dd.addEventListener('scroll', () => { dropdownState.sprint.scroll = dd.scrollTop; });

  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    dropdownState.sprint.open = !dropdownState.sprint.open;
    dd.classList.toggle('open', dropdownState.sprint.open);
    btn.setAttribute('aria-expanded', String(dropdownState.sprint.open));
  });

  dd.addEventListener('click', (e) => {
    const presetBtn = e.target.closest('[data-preset]');
    if (!presetBtn) return;
    filters.sprints = applySprintPresets(sprintFilterNumbers(sprints, tasks), presetBtn.dataset.preset);
    dropdownState.sprint.open = false;   // a preset is a decision → close
    dropdownState.sprint.scroll = 0;
    dd.classList.remove('open');
    onChange(filters);
  });

  dd.addEventListener('change', (e) => {
    if (!e.target.matches('input[type="checkbox"]')) return;
    filters.sprints = [...dd.querySelectorAll('input[type="checkbox"]:checked')]
      .map(cb => parseInt(cb.value, 10));
    dropdownState.sprint.open = true;    // keep open across the re-render
    dropdownState.sprint.scroll = dd.scrollTop;
    onChange(filters);
  });
}

// ── Type selector (sprint-style dropdown, TPT109) ──
// Replaces the old .bf-types icon-toggle strip. Reuses the dropdownState registry and
// shared outside-click handler above — see § Type selector UI in tt-web-filters.md.
// workspace-tasks.js now mounts this same bar in workspace scope (TPT447).

export function renderTypeSelector(selected, opts = {}) {
  const open = opts.open !== undefined ? opts.open : dropdownState.type.open;
  const sel = selected || [];
  const countLabel = sel.length ? `${sel.length} selected` : 'All';

  let html = '<div class="bf-type-wrap" id="bf-type-wrap">';
  html += `<button type="button" class="btn btn-ghost btn-sm bf-type-trigger" id="bf-type-btn" aria-haspopup="true" aria-expanded="${open}">Type: ${escapeHtml(countLabel)} <span style="font-size:0.7rem">&#9662;</span></button>`;
  html += `<div class="ws-dropdown bf-type-dropdown${open ? ' open' : ''}" id="bf-type-dropdown">`;
  for (const t of TYPES) {
    // Empty selection means "all" (same convention every dimension in this file uses) —
    // every row renders checked in that state, never all-unchecked beside an unfiltered board.
    const checked = sel.length ? sel.includes(t) : true;
    html += `<label class="bf-type-option"><input type="checkbox" value="${t}" ${checked ? 'checked' : ''}> ${getTypeIcon(t, 16)} ${escapeHtml(TYPE_LABELS[t])}</label>`;
  }
  html += '</div></div>';
  return html;
}

function bindTypeSelector(filters, onChange) {
  const btn = document.getElementById('bf-type-btn');
  const dd = document.getElementById('bf-type-dropdown');
  if (!btn || !dd) return;

  if (dropdownState.type.open) dd.scrollTop = dropdownState.type.scroll;
  dd.addEventListener('scroll', () => { dropdownState.type.scroll = dd.scrollTop; });

  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    dropdownState.type.open = !dropdownState.type.open;
    dd.classList.toggle('open', dropdownState.type.open);
    btn.setAttribute('aria-expanded', String(dropdownState.type.open));
  });

  dd.addEventListener('change', (e) => {
    if (!e.target.matches('input[type="checkbox"]')) return;
    const checked = [...dd.querySelectorAll('input[type="checkbox"]:checked')].map(cb => cb.value);
    // All 4 checked (same as none active before) → empty (= all), matching the old
    // toggle-strip rule this replaces.
    filters.types = checked.length === TYPES.length ? [] : checked;
    dropdownState.type.open = true;      // keep open across the re-render
    dropdownState.type.scroll = dd.scrollTop;
    onChange(filters);
  });
}

export function renderUrgencySelector(selected = [], language) {
  const copy = urgencyCopy(language);
  const label = selected.length ? selected.map(n => urgencyLabel(n, language)).join(', ') : copy.all;
  let html = `<div class="bf-type-wrap" id="bf-urgency-wrap"><button type="button" class="btn btn-ghost btn-sm bf-type-trigger" id="bf-urgency-btn" aria-haspopup="true" aria-expanded="${dropdownState.urgency.open}">${copy.field}: ${label} <span style="font-size:0.7rem">&#9662;</span></button>`;
  html += `<div class="ws-dropdown bf-type-dropdown${dropdownState.urgency.open ? ' open' : ''}" id="bf-urgency-dropdown">`;
  for (const value of URGENCY_VALUES) {
    html += `<label><input type="checkbox" value="${value}"${!selected.length || selected.includes(value) ? ' checked' : ''}> ${copy.levels[value]}</label>`;
  }
  return html + '</div></div>';
}

function bindUrgencySelector(filters, onChange) {
  const btn = document.getElementById('bf-urgency-btn');
  const dd = document.getElementById('bf-urgency-dropdown');
  if (!btn || !dd) return;
  dd.scrollTop = dropdownState.urgency.scroll;
  btn.addEventListener('click', () => {
    dropdownState.urgency.open = !dropdownState.urgency.open;
    dd.classList.toggle('open', dropdownState.urgency.open);
    btn.setAttribute('aria-expanded', String(dropdownState.urgency.open));
  });
  dd.addEventListener('change', e => {
    if (!e.target.matches('input[type="checkbox"]')) return;
    const checked = [...dd.querySelectorAll('input[type="checkbox"]:checked')].map(cb => Number(cb.value));
    filters.urgencies = checked.length === URGENCY_VALUES.length ? [] : checked;
    dropdownState.urgency.open = true;
    dropdownState.urgency.scroll = dd.scrollTop;
    onChange(filters);
  });
}

// ── Assignee selector (sprint/type-style dropdown, TPT110) ──
// Replaces the old .bf-assignee-btn avatar toggle strip. Reuses the dropdownState
// registry and shared outside-click handler above — see § Assignee filter in
// tt-web-filters.md. Unlike type/status, there is NO "all selected ⇒ reset to []"
// rule here (see renderAssigneeSelector below) — the "All assignees" row is what
// represents the unfiltered state instead.

// Small module-private glyph for the Unassigned pseudo-member (uid 0) — deliberately
// NOT renderAvatarHtml(null, size): that branch draws an "add avatar" plus-cross with
// pointer/hover-to-primary styling meant for a profile picker, not a filter row.
function unassignedAvatarHtml(size) {
  return `<div class="avatar bf-avatar-unassigned" style="width:${size}px;height:${size}px;font-size:${Math.round(size * 0.45)}px;">?</div>`;
}

// Distinct glyph for the "All assignees" row — an em-dash, never the same "?" as
// Unassigned above, so the two rows read as different concepts at a glance.
function allAssigneesGlyphHtml(size) {
  return `<div class="avatar bf-avatar-unassigned" style="width:${size}px;height:${size}px;font-size:${Math.round(size * 0.45)}px;">&#8212;</div>`;
}

export function renderAssigneeSelector(selected, members, opts = {}) {
  const open = opts.open !== undefined ? opts.open : dropdownState.assignee.open;
  const sel = selected || [];
  const { shown, overflow } = assigneeStackEntries(sel, members, 5);

  let html = '<div class="bf-assignee-wrap" id="bf-assignee-wrap">';
  html += `<button type="button" class="btn btn-ghost btn-sm bf-assignee-trigger" id="bf-assignee-btn" aria-haspopup="true" aria-expanded="${open}">`;
  if (sel.length === 0) {
    html += 'Assignee: All';
  } else {
    const names = sel.map(uid => {
      if (uid === 0) return 'Unassigned';
      const m = (members || []).find(mm => mm.user_id === uid);
      return m ? (m.name || m.email || '#' + uid) : '#' + uid;
    });
    html += `<span class="bf-avatar-stack" title="${escapeHtml(names.join(', '))}" aria-label="${escapeHtml(names.join(', '))}">`;
    for (const { uid, member } of shown) {
      html += uid === 0 ? unassignedAvatarHtml(20) : renderAvatarHtml(member, 20);
    }
    if (overflow > 0) html += `<span class="bf-avatar-more" style="width:20px;height:20px;">+${overflow}</span>`;
    html += '</span>';
  }
  html += ' <span style="font-size:0.7rem">&#9662;</span></button>';

  html += `<div class="ws-dropdown bf-assignee-dropdown${open ? ' open' : ''}" id="bf-assignee-dropdown">`;
  html += `<label class="bf-assignee-option"><input type="checkbox" value="" ${sel.length === 0 ? 'checked' : ''}>${allAssigneesGlyphHtml(24)}<span class="bf-assignee-name">All assignees</span></label>`;
  html += `<label class="bf-assignee-option"><input type="checkbox" value="0" ${sel.includes(0) ? 'checked' : ''}>${unassignedAvatarHtml(24)}<span class="bf-assignee-name">Unassigned</span></label>`;
  for (const m of (members || [])) {
    // (C1405) skip pending invite rows — see the identical guard this replaces below.
    if (!m.user_id) continue;
    const uid = m.user_id;
    html += `<label class="bf-assignee-option"><input type="checkbox" value="${uid}" ${sel.includes(uid) ? 'checked' : ''}>${renderAvatarHtml(m, 24)}<span class="bf-assignee-name">${escapeHtml(m.name || m.email || '#' + uid)}</span></label>`;
  }
  html += '</div></div>';
  return html;
}

function bindAssigneeSelector(filters, onChange) {
  const btn = document.getElementById('bf-assignee-btn');
  const dd = document.getElementById('bf-assignee-dropdown');
  if (!btn || !dd) return;

  if (dropdownState.assignee.open) dd.scrollTop = dropdownState.assignee.scroll;
  dd.addEventListener('scroll', () => { dropdownState.assignee.scroll = dd.scrollTop; });

  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    dropdownState.assignee.open = !dropdownState.assignee.open;
    dd.classList.toggle('open', dropdownState.assignee.open);
    btn.setAttribute('aria-expanded', String(dropdownState.assignee.open));
  });

  dd.addEventListener('change', (e) => {
    if (!e.target.matches('input[type="checkbox"]')) return;
    const value = e.target.value;
    filters.assignees = value === '' ? [] : toggleAssignee(filters.assignees, parseInt(value, 10));
    dropdownState.assignee.open = true;    // keep open across the re-render
    dropdownState.assignee.scroll = dd.scrollTop;
    onChange(filters);
  });
}

// ── Status selector (sprint/type/assignee-style dropdown, TPT108) ──
// Replaces the old .bf-statuses checkbox strip. Reuses the dropdownState registry and
// shared outside-click/Escape handler above — see § Status selector UI in tt-web-filters.md.
// Counts come from computeStatusCounts() (TPT107, previously unused): faceted, every OTHER
// filter dimension applies, status itself doesn't, so each row answers "how many tasks
// would I see if I picked only this status".

// Trigger label — ported verbatim from the Task App's statusFilterLabel() (C1547):
// 0 selected -> "All statuses (n)" using the facet total; exactly 1 -> "{Label} (n)" using
// that status's own count; 2+ -> "N statuses (n)" where the leading N is how many statuses
// are selected and the parenthesised n is the SUM of their counts — two different numbers.
// counts === null (workspace scope, TPT447: the list is server-paged so a facet count would be
// wrong) drops every "(n)" suffix.
export function statusFilterLabel(selected, counts) {
  const sel = selected || [];
  if (counts === null) {
    if (sel.length === 0) return 'All statuses';
    if (sel.length === 1) return statusLabel(sel[0]);
    return `${sel.length} statuses`;
  }
  const c = counts || {};
  if (sel.length === 0) return `All statuses (${c.all || 0})`;
  if (sel.length === 1) return `${statusLabel(sel[0])} (${c[sel[0]] || 0})`;
  const sum = sel.reduce((s, n) => s + (c[n] || 0), 0);
  return `${sel.length} statuses (${sum})`;
}

// One row's count — `''` is the All row, i.e. the facet total. Shared by renderStatusSelector()
// and syncStatusCounts() so the full render and the in-place patch can never disagree.
function statusCount(counts, name) {
  return (name === '' ? counts.all : counts[name]) || 0;
}

// Counts: `opts.statusCounts` when the caller already has them — the board passes
// GET /tasks/facets' status_counts (TPT597), same `{ all, <status>: n }` shape. Otherwise
// computeStatusCounts(tasks, filters), which re-applies every non-status dimension of `filters`
// itself, so `tasks` must then be the unfiltered project task list.
export function renderStatusSelector(statuses, tasks, filters, opts = {}) {
  const open = opts.open !== undefined ? opts.open : dropdownState.status.open;
  const names = statusNames(statuses);
  const showCounts = opts.showStatusCounts !== false;
  const counts = showCounts ? (opts.statusCounts || computeStatusCounts(tasks, filters)) : null;
  const countHtml = (name) => (showCounts ? `<span class="bf-status-count">(${statusCount(counts, name)})</span>` : '');
  const sel = filters.statuses || [];
  const label = statusFilterLabel(sel, counts);

  let html = '<div class="bf-status-wrap" id="bf-status-wrap">';
  // #bf-status-label is what syncStatusCounts() rewrites in place.
  html += `<button type="button" class="btn btn-ghost btn-sm bf-status-trigger" id="bf-status-btn" aria-haspopup="true" aria-expanded="${open}" title="Filter by status" aria-label="Filter by status"><span id="bf-status-label">${escapeHtml(label)}</span> <span style="font-size:0.7rem">&#9662;</span></button>`;
  html += `<div class="ws-dropdown bf-status-dropdown${open ? ' open' : ''}" id="bf-status-dropdown">`;
  if (names.length === 0) {
    html += '<div class="text-faint" style="padding:0.5rem;font-size:0.8rem;">No statuses yet</div>';
  } else {
    const allChecked = isStatusRowChecked(sel, '') ? 'checked' : '';
    html += `<label class="bf-status-option bf-status-option--all"><input type="checkbox" value="" ${allChecked}><span>All statuses</span>${countHtml('')}</label>`;
    for (const s of names) {
      const checked = isStatusRowChecked(sel, s) ? 'checked' : '';
      html += `<label class="bf-status-option"><input type="checkbox" value="${escapeHtml(s)}" ${checked}><span>${escapeHtml(statusLabel(s))}</span>${countHtml(s)}</label>`;
    }
  }
  html += '</div></div>';
  return html;
}

// In-place count refresh (TPT597). The board's facet counts arrive after its filter bar was
// already rebuilt for the new selection (or, for a search, chip-synced — C1524), so only the
// trigger label and each row's "(n)" change. No node is replaced: focus, an open popover, its
// scroll position and a click in progress all survive, and #bf-search is never looked up.
export function syncStatusCounts(filters, counts) {
  const label = document.getElementById('bf-status-label');
  if (label) label.textContent = statusFilterLabel(filters.statuses || [], counts);
  const dd = document.getElementById('bf-status-dropdown');
  if (!dd || !counts) return;
  for (const cb of dd.querySelectorAll('input[type="checkbox"]')) {
    const countEl = cb.parentElement && cb.parentElement.querySelector('.bf-status-count');
    if (countEl) countEl.textContent = `(${statusCount(counts, cb.value)})`;
  }
}

function bindStatusSelector(filters, onChange, statuses) {
  const btn = document.getElementById('bf-status-btn');
  const dd = document.getElementById('bf-status-dropdown');
  if (!btn || !dd) return;
  const names = statusNames(statuses);

  if (dropdownState.status.open) dd.scrollTop = dropdownState.status.scroll;
  dd.addEventListener('scroll', () => { dropdownState.status.scroll = dd.scrollTop; });

  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    dropdownState.status.open = !dropdownState.status.open;
    dd.classList.toggle('open', dropdownState.status.open);
    btn.setAttribute('aria-expanded', String(dropdownState.status.open));
  });

  // Per-target toggle math (nextStatusSelection, TPT107) rather than a full re-scan of
  // checked boxes — a re-scan would have to special-case the All row's value="" checkbox,
  // and would re-sort a restored ?status= selection into registry order.
  dd.addEventListener('change', (e) => {
    if (!e.target.matches('input[type="checkbox"]')) return;
    filters.statuses = nextStatusSelection(filters.statuses, e.target.value, e.target.checked, names);
    dropdownState.status.open = true;    // keep open across the re-render
    dropdownState.status.scroll = dd.scrollTop;
    onChange(filters);
  });
}

// ── Project selector (workspace scope only, TPT447) ──
// Stands in for the per-project sprint selector. Same registry/outside-click/Escape wiring
// as the other dropdowns. `projects` = [{ id, name, color }] supplied by the caller. Like
// assignee, an empty selection means "all" and rows are checked only when really selected.

export function renderProjectSelector(projects, selected, opts = {}) {
  const open = opts.open !== undefined ? opts.open : dropdownState.project.open;
  const sel = selected || [];
  const countLabel = sel.length ? `${sel.length} selected` : 'All';

  let html = '<div class="bf-project-wrap" id="bf-project-wrap">';
  html += `<button type="button" class="btn btn-ghost btn-sm bf-project-trigger" id="bf-project-btn" aria-haspopup="true" aria-expanded="${open}">Project: ${escapeHtml(countLabel)} <span style="font-size:0.7rem">&#9662;</span></button>`;
  html += `<div class="ws-dropdown bf-project-dropdown${open ? ' open' : ''}" id="bf-project-dropdown">`;
  if (!projects || projects.length === 0) {
    html += '<div class="text-faint" style="padding:0.5rem;font-size:0.8rem;">No projects</div>';
  } else {
    html += `<label class="bf-project-option bf-project-option--all"><input type="checkbox" value="" ${sel.length === 0 ? 'checked' : ''}><span>All projects</span></label>`;
    for (const p of projects) {
      html += `<label class="bf-project-option"><input type="checkbox" value="${p.id}" ${sel.includes(p.id) ? 'checked' : ''}><span class="bf-project-dot" style="background:${escapeHtml(p.color || 'var(--c-primary)')}"></span><span class="bf-project-name">${escapeHtml(p.name)}</span></label>`;
    }
  }
  html += '</div></div>';
  return html;
}

function bindProjectSelector(filters, onChange) {
  const btn = document.getElementById('bf-project-btn');
  const dd = document.getElementById('bf-project-dropdown');
  if (!btn || !dd) return;

  if (dropdownState.project.open) dd.scrollTop = dropdownState.project.scroll;
  dd.addEventListener('scroll', () => { dropdownState.project.scroll = dd.scrollTop; });

  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    dropdownState.project.open = !dropdownState.project.open;
    dd.classList.toggle('open', dropdownState.project.open);
    btn.setAttribute('aria-expanded', String(dropdownState.project.open));
  });

  dd.addEventListener('change', (e) => {
    if (!e.target.matches('input[type="checkbox"]')) return;
    const value = e.target.value;
    // New arrays only, never mutate in place (see toggleAssignee).
    if (value === '') filters.projects = [];
    else {
      const id = parseInt(value, 10);
      const cur = filters.projects || [];
      filters.projects = cur.includes(id) ? cur.filter(p => p !== id) : [...cur, id];
    }
    dropdownState.project.open = true;   // keep open across the re-render
    dropdownState.project.scroll = dd.scrollTop;
    onChange(filters);
  });
}

// ── URL serialization ──
//
// Workspace scope (opts.scope === 'workspace', TPT447) writes `project=<ids>` instead of
// `sprint`, and an empty status selection serializes as `status=all` — same idea as the
// board's `sprint=all`: lets a reload tell "user picked all statuses" apart from "no status
// param yet" (caller then applies its open-statuses default).

export function filtersToParams(filters, opts = {}) {
  const p = new URLSearchParams();
  const ws = isWorkspace(opts);
  if (filters.q) p.set('q', filters.q);
  if (filters.types.length) p.set('type', filters.types.join(','));
  if (filters.statuses.length) p.set('status', filters.statuses.join(','));
  else if (ws) p.set('status', 'all');
  if (filters.assignees.length) p.set('assignee', filters.assignees.join(','));
  if (filters.tags.length) p.set('tag', filters.tags.join(','));
  if (!ws && filters.urgencies?.length) p.set('urgency', filters.urgencies.join(','));
  if (ws) {
    if ((filters.projects || []).length) p.set('project', filters.projects.join(','));
    return p;
  }
  // 'all' sentinel: empty selection always serializes as explicit 'all', so a
  // reload can tell "user picked All Sprints" apart from "no sprint param yet"
  // (the latter → caller applies its own Recent-5 default, see board.js).
  p.set('sprint', filters.sprints.length ? filters.sprints.join(',') : 'all');
  return p;
}

// Filter state → GET /api/workspace/tasks query (TPT446 params). The caller appends
// limit/offset. Assignee ids pass through as-is: 0 is the API's "unassigned" token.
export function workspaceQueryParams(filters) {
  const p = new URLSearchParams();
  if (filters.q) p.set('q', filters.q);
  if (filters.types.length) p.set('type', filters.types.join(','));
  if (filters.statuses.length) p.set('status', filters.statuses.join(','));
  if (filters.assignees.length) p.set('assignee', filters.assignees.join(','));
  if (filters.tags.length) p.set('tag', filters.tags.join(','));
  if ((filters.projects || []).length) p.set('project_id', filters.projects.join(','));
  return p;
}

// Filter state → GET /projects/:id/tasks query (TPT595 params): the board's server-side
// filtering (TPT597). GET /tasks/facets takes the same string and ignores `fields`. Sprint
// numbers travel as `priority` (task.priority === sprint.number, 0 = backlog) and assignees as
// `assignee_in` (0 = unassigned) — never `assignee`, which is the API's personal-scope param.
// Empty dimensions are omitted (no `sprint=all`-style sentinel: an empty selection is simply no
// filter), so FILTER_DEFAULTS yields only `fields=board` — the card column set, whose description
// is capped at 600 characters and flagged by `description_truncated`.
export function filtersToQuery(filters) {
  const p = new URLSearchParams();
  if (filters.q) p.set('q', filters.q);
  if (filters.types.length) p.set('type', filters.types.join(','));
  if (filters.statuses.length) p.set('status', filters.statuses.join(','));
  if (filters.assignees.length) p.set('assignee_in', filters.assignees.join(','));
  if (filters.tags.length) p.set('tag', filters.tags.join(','));
  if (filters.sprints.length) p.set('priority', filters.sprints.join(','));
  if (filters.urgencies?.length) p.set('urgency', filters.urgencies.join(','));
  p.set('fields', 'board');
  return p;
}

// Workspace scope (TPT453): drop every selected project/status/assignee/tag the server's
// narrowed option lists no longer offer, so a URL reload can never restore a value the user
// cannot see. `options` = { projects: [ids], statuses: [{name}], members: [{user_id}], tags: [names] }.
// A status selection that pruning would empty falls back to `defaultStatuses` (never widens
// to "all"); an already-empty selection stays empty. Returns { filters, changed } — a new
// object, the input is never mutated.
export function pruneWorkspaceFilters(filters, options, defaultStatuses = []) {
  const validProjects = new Set((options.projects || []).map(Number));
  const validStatuses = new Set((options.statuses || []).map(s => s.name));
  const validAssignees = new Set((options.members || []).map(m => m.user_id));
  const validTags = new Set(options.tags || []);
  const next = { ...filters };
  next.projects = (filters.projects || []).filter(id => validProjects.has(Number(id)));
  next.statuses = (filters.statuses || []).filter(n => validStatuses.has(n));
  if (next.statuses.length === 0 && (filters.statuses || []).length > 0) {
    next.statuses = defaultStatuses.filter(n => validStatuses.has(n));
  }
  next.assignees = (filters.assignees || []).filter(id => id === 0 || validAssignees.has(id));
  next.tags = (filters.tags || []).filter(t => validTags.has(t));
  const same = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);
  const changed = !(
    same(next.projects, filters.projects || []) && same(next.statuses, filters.statuses || [])
    && same(next.assignees, filters.assignees || []) && same(next.tags, filters.tags || [])
  );
  return { filters: next, changed };
}

// Does this URLSearchParams carry ANY board-filter dimension at all — used by board.js
// (TPT25) to decide "explicit deep link, trust the URL" vs. "bare hash from a route
// navigation that dropped the query, fall back to this project's saved prefs instead".
// `sprint` alone counts: filtersToParams() always writes it, so a URL built by this app
// itself (e.g. a copied board link) already carries it even with every other dimension
// empty.
export function hasFilterParams(params) {
  return FILTER_PARAM_KEYS.some(k => params.has(k));
}

export function filtersFromParams(params, opts = {}) {
  const sprintRaw = params.get('sprint');
  const statusRaw = params.get('status');
  const ws = isWorkspace(opts);
  const out = {
    q: params.get('q') || '',
    types: params.get('type') ? params.get('type').split(',').filter(Boolean) : [],
    statuses: statusRaw && !(ws && statusRaw === 'all') ? statusRaw.split(',').filter(Boolean) : [],
    // (C1405) .filter(Boolean) BEFORE .map(Number) — a trailing/empty comma segment (hand-
    // edited or truncated URL, e.g. "assignee=3,") maps to 0, which is a MEANINGFUL value
    // here (Unassigned) — without the string-level filter first it'd silently add an
    // Unassigned selection that was never picked. filtersToParams() never emits a trailing
    // comma itself, so this only guards hand-built/malformed URLs.
    assignees: params.get('assignee') ? params.get('assignee').split(',').filter(Boolean).map(Number).filter(n => !isNaN(n)) : [],
    tags: params.get('tag') ? params.get('tag').split(',').filter(Boolean) : [],
    urgencies: !ws && params.get('urgency') ? [...new Set(params.get('urgency').split(',').filter(v => /^[0-4]$/.test(v)).map(Number))] : [],
    // Same guard as assignees above — 0 means Backlog here, equally meaningful.
    sprints: sprintRaw && sprintRaw !== 'all' ? sprintRaw.split(',').filter(Boolean).map(Number).filter(n => !isNaN(n)) : [],
  };
  // `projects` exists only in workspace scope (TPT447) — the board's shape is unchanged.
  if (ws) out.projects = params.get('project') ? params.get('project').split(',').filter(Boolean).map(Number).filter(n => Number.isInteger(n) && n > 0) : [];
  return out;
}

// ── Check if any filter active ──

export function hasActiveFilters(filters) {
  return !!(filters.q || filters.types.length || filters.statuses.length || filters.assignees.length || filters.tags.length || (filters.urgencies || []).length || filters.sprints.length || (filters.projects || []).length);
}

// Edit Board (canEditWorkflow-gated, kanban-only) — two questions since TPT9059:
// emitsEditBoardBtn() is whether the bar carries #bf-edit-board-btn at all (board scope,
// workflow-editable), showsEditBoardBtn() whether it is visible right now (kanban view). The
// button is always emitted for an eligible bar and `hidden`-toggled by the view — the C1524
// pattern — so a view switch (syncEditBoardButton) patches it in place instead of rebuilding
// the bar. Shared by renderBoardFilters, syncFilterChips and syncEditBoardButton so all three
// agree on when .bf-right-group has non-Clear-All content.
function emitsEditBoardBtn(opts = {}) {
  return !isWorkspace(opts) && !!opts.canEditWorkflow;
}
function showsEditBoardBtn(opts = {}) {
  return emitsEditBoardBtn(opts) && opts.activeView === 'kanban';
}

// Inner HTML of the active-filter chip row — split out of renderBoardFilters (C1524) so a
// search-only change (syncFilterChips) can refresh just this, without re-emitting #bf-search.
export function renderFilterChips(filters, members, sprints, opts = {}) {
  let html = '';
  if (filters.q) {
    html += `<span class="bf-chip">Search: "${escapeHtml(filters.q)}" <span class="bf-chip-remove" data-filter="q">&times;</span></span>`;
  }
  for (const t of filters.types) {
    html += `<span class="bf-chip">${TYPE_LABELS[t] || t} <span class="bf-chip-remove" data-filter="type" data-value="${t}">&times;</span></span>`;
  }
  for (const value of (filters.urgencies || [])) {
    html += `<span class="bf-chip">${urgencyCopy(opts.language).field}: ${urgencyLabel(value, opts.language)} <span class="bf-chip-remove" data-filter="urgency" data-value="${value}">&times;</span></span>`;
  }
  for (const uid of filters.assignees) {
    const label = uid === 0 ? 'Unassigned' : (() => {
      const m = members.find(m => (m.user_id || m.id) === uid);
      return m ? (m.name || m.email || '#' + uid) : '#' + uid;
    })();
    html += `<span class="bf-chip">${escapeHtml(label)} <span class="bf-chip-remove" data-filter="assignee" data-value="${uid}">&times;</span></span>`;
  }
  for (const s of filters.statuses) {
    html += `<span class="bf-chip">${escapeHtml(statusLabel(s))} <span class="bf-chip-remove" data-filter="status" data-value="${escapeHtml(s)}">&times;</span></span>`;
  }
  if (isWorkspace(opts)) {
    for (const id of (filters.projects || [])) {
      const p = (opts.projects || []).find(pr => pr.id === id);
      html += `<span class="bf-chip">${escapeHtml(p ? p.name : '#' + id)} <span class="bf-chip-remove" data-filter="project" data-value="${id}">&times;</span></span>`;
    }
  } else if (opts.sprintsEnabled !== false) {
    for (const n of filters.sprints) {
      html += `<span class="bf-chip">${escapeHtml(sprintLabel(sprints, n, opts.groupLabelSingular || 'Sprint'))} <span class="bf-chip-remove" data-filter="sprint" data-value="${n}">&times;</span></span>`;
    }
  }
  return html;
}

// ── Render ──

export function renderBoardFilters(filters, members, allTags, sprints, tasks, statuses, opts = {}) {
  let html = '<div class="bf-bar" id="bf-bar">';

  // Search
  html += `<input type="text" class="form-input form-input-sm bf-search" id="bf-search" maxlength="${MAX_SEARCH_LENGTH}" placeholder="Search tasks..." value="${escapeHtml(filters.q)}">`;

  // Sprint selector — persistent across all view modes (C1166). Label follows the
  // project's task_group_label (C1234) — omitting opts.open here is deliberate, it
  // keeps falling back to the registry's dropdownState.sprint.open (see renderSprintSelector).
  // C1334 — hidden entirely when the project's sprints are off; board.js already forces
  // filters.sprints = [] in that case, so this is belt-and-braces on top of that.
  // TPT447 — workspace scope swaps it for a Project multi-select (sprints are per-project).
  if (isWorkspace(opts)) {
    html += renderProjectSelector(opts.projects, filters.projects);
  } else if (opts.sprintsEnabled !== false) {
    html += renderSprintSelector(sprints, tasks, filters.sprints, { groupLabel: opts.groupLabel, groupLabelSingular: opts.groupLabelSingular });
  }

  // Type selector — sprint-style dropdown (TPT109, was an icon-toggle strip).
  // Omitting opts.open here is deliberate, same reasoning as the sprint selector above.
  html += renderTypeSelector(filters.types);
  if (!isWorkspace(opts)) html += renderUrgencySelector(filters.urgencies, opts.language);

  // Assignee selector — sprint/type-style dropdown (TPT110, was an avatar-toggle strip).
  if (members.length > 0) {
    html += renderAssigneeSelector(filters.assignees, members);
  }

  // Status selector — sprint/type/assignee-style dropdown (TPT108, was a checkbox strip).
  // Omitting opts.open here is deliberate, same reasoning as the siblings above.
  // opts.statusCounts — the board's facet counts (TPT597); absent, they're computed from `tasks`.
  html += renderStatusSelector(statuses, tasks, filters, { showStatusCounts: opts.showStatusCounts, statusCounts: opts.statusCounts });

  // Tag typeahead
  html += '<div class="bf-tag-wrap" id="bf-tag-wrap">';
  for (const tag of filters.tags) {
    html += `<span class="bf-tag-chip tag"><span>${escapeHtml(tag)}</span> <span class="bf-tag-chip-x" data-tag="${escapeHtml(tag)}">&times;</span></span>`;
  }
  html += `<input type="text" class="form-input form-input-sm bf-tag-input" id="bf-tag-input" maxlength="${MAX_TAG_LENGTH}" placeholder="Tag..." style="width:100px;">`;
  html += '<div class="bf-tag-suggestions" id="bf-tag-suggestions"></div>';
  html += '</div>';

  // Right-aligned group: Edit Board (C1183) + Clear All — wrapped together so the
  // group, not `.bf-clear` alone, carries the margin-left:auto push. Clear All is
  // conditional (hasActiveFilters), so anchoring the auto-margin on it alone would
  // shift Edit Board around whenever active filters come and go.
  // C1524 — group + Clear All + chip row below are now always emitted, hidden-toggled
  // instead of conditionally present in the markup: syncFilterChips() flips `hidden` in
  // place for a search-only change, no innerHTML wipe that would take #bf-search's focus
  // with it. TPT9059 — #bf-edit-board-btn works the same way: emitted for every eligible bar
  // (emitsEditBoardBtn), `hidden` unless the kanban view is active (showsEditBoardBtn), so a
  // view switch patches it via syncEditBoardButton() instead of rebuilding the bar. See
  // styles.css for the required `[hidden]{display:none}` companion rules (.bf-right-group/
  // .bf-chips set display:flex, .bf-edit-board-btn inline-flex — all out-specify the UA default).
  const showEditBoard = showsEditBoardBtn(opts);
  const showClear = hasActiveFilters(filters);
  html += `<div class="bf-right-group" id="bf-right-group"${showEditBoard || showClear ? '' : ' hidden'}>`;
  if (emitsEditBoardBtn(opts)) {
    const active = !!opts.editBoardMode;
    html += `<button type="button" class="bf-edit-board-btn${active ? ' bf-edit-board-active' : ''}" id="bf-edit-board-btn" title="Edit Board" aria-label="Edit Board" aria-pressed="${active}"${showEditBoard ? '' : ' hidden'}>`
      + '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>'
      + '</button>';
  }
  html += `<a href="#" class="bf-clear" id="bf-clear"${showClear ? '' : ' hidden'}>Clear All</a>`;
  html += '</div>';

  html += '</div>';

  // Chips below bar — always emitted, hidden-toggled (C1524, see comment above).
  html += `<div class="bf-chips" id="bf-chips"${showClear ? '' : ' hidden'}>`;
  html += renderFilterChips(filters, members, sprints, opts);
  html += '</div>';

  return html;
}

// The complete set of bar content that depends on filters.q — the search-only refresh path
// (C1524). Never resolves or touches #bf-search: board.js's onFilterChange calls this instead
// of a full refreshFilterBar() rebuild when changedKey === 'q'.
export function syncFilterChips(filters, members, sprints, opts = {}) {
  const active = hasActiveFilters(filters);
  const chipsEl = document.getElementById('bf-chips');
  if (chipsEl) {
    chipsEl.innerHTML = renderFilterChips(filters, members, sprints, opts);
    chipsEl.hidden = !active;
  }
  const clearEl = document.getElementById('bf-clear');
  if (clearEl) clearEl.hidden = !active;
  const groupEl = document.getElementById('bf-right-group');
  if (groupEl) groupEl.hidden = !active && !showsEditBoardBtn(opts);
}

// The Edit Board button's view-dependent state (TPT9059): a view switch (board.js's
// onViewChange) and the toggle itself (onToggleEditBoard) patch the existing button in place —
// `hidden` from showsEditBoardBtn(opts), pressed state from opts.editBoardMode — plus
// .bf-right-group's `hidden`, by the same formula renderBoardFilters/syncFilterChips use. Never
// resolves or touches #bf-search: a full refreshFilterBar() rebuild on a switch would drop
// search text typed inside the input's 300ms debounce. A bar with no button (read-only member,
// workspace scope) only gets the group patched.
export function syncEditBoardButton(filters, opts = {}) {
  const show = showsEditBoardBtn(opts);
  const active = !!opts.editBoardMode;
  const btn = document.getElementById('bf-edit-board-btn');
  if (btn) {
    btn.hidden = !show;
    btn.classList.toggle('bf-edit-board-active', active);
    btn.setAttribute('aria-pressed', String(active));
  }
  const groupEl = document.getElementById('bf-right-group');
  if (groupEl) groupEl.hidden = !hasActiveFilters(filters) && !show;
}

// ── Focus preservation across a full bar rebuild (C1524) ──
//
// refreshFilterBar() (board.js) still does a full innerHTML wipe for every non-q filter
// change, including a data reload through loadBoardTasks() — fired ~220ms after a task-detail
// drawer closes, which can land mid-keystroke in #bf-search. captureFilterFocus()/restoreFilterFocus() bracket
// every such rebuild so the focused control (if any, and if it's inside this bar) survives.
// Scoped to #bf-bar's own ids, not board.js's #board-filters wrapper — this component owns
// its own focus contract.

export function captureFilterFocus() {
  const el = document.activeElement;
  if (!el || !el.id || !el.closest || !el.closest('#bf-bar')) return null;
  const snap = { id: el.id };
  if (typeof el.selectionStart === 'number') {
    snap.start = el.selectionStart;
    snap.end = el.selectionEnd;
  }
  // Carry the live value only for #bf-search: on the reload-during-typing race the
  // rebuilt input would otherwise show the last-committed (trimmed) filters.q, silently
  // dropping up to 300ms of keystrokes typed since the debounce last fired.
  if (el.id === 'bf-search') snap.value = el.value;
  return snap;
}

export function restoreFilterFocus(snap) {
  if (!snap) return;
  const el = document.getElementById(snap.id);
  if (!el) return;
  if (snap.value !== undefined && el.value !== snap.value) el.value = snap.value;
  el.focus({ preventScroll: true });
  if (typeof snap.start === 'number') {
    try {
      const len = el.value.length;
      el.setSelectionRange(Math.min(snap.start, len), Math.min(snap.end, len));
    } catch (_) { /* non-text input, e.g. a checkbox — nothing to restore */ }
  }
}

// ── Event binding ──

// Returns this binding's owner token (TPT643). The page keeps the token of its latest bind and
// passes it to unbindBoardFilters() on teardown.
export function bindBoardFilters(filters, onChange, members, allTags, sprints, tasks, statuses, opts = {}, onToggleEditBoard) {
  const token = {};
  filterBindingOwner = token;
  // Detach unconditionally, even if #bf-bar is missing this render — otherwise
  // a stale document-level mousedown listener leaks on every filter change.
  detachFilterOutsideHandler();

  const bar = document.getElementById('bf-bar');
  if (!bar) return token;

  // Re-registers the ONE shared outside-click listener covering every dropdown in
  // dropdownState (sprint, type, …) — deferred so the opening mousedown that triggered
  // this bind (e.g. a trigger-button click bubbling to document) doesn't instantly
  // close the dropdown it just opened.
  attachFilterOutsideHandler(token);

  // Search
  const searchEl = document.getElementById('bf-search');
  if (searchEl) {
    // (C1524) changedKey 'q' tells board.js's onFilterChange to call syncFilterChips()
    // instead of a full refreshFilterBar() rebuild — #bf-search stays untouched, keeping
    // focus/caret. Trim-equality guard: a value whose trimmed form matches the already-
    // committed filters.q (e.g. a trailing space just typed) fires no re-render at all,
    // so that space is never silently eaten by a rebuild reading the trimmed value back.
    const debouncedSearch = debounce(() => {
      const next = searchEl.value.trim();
      if (next === filters.q) return;
      filters.q = next;
      onChange(filters, 'q');
    }, 300);
    searchEl.addEventListener('input', debouncedSearch);
  }

  if (isWorkspace(opts)) {
    // TPT447 — no sprint selector; same stale-flag reset as the sprints-off branch below.
    dropdownState.sprint.open = false;
    dropdownState.sprint.scroll = 0;
    bindProjectSelector(filters, onChange);
  } else if (opts.sprintsEnabled === false) {
    // C1334 — selector not rendered. Reset the registry's open/scroll state here (not in
    // renderBoardFilters, which must stay pure): those flags survive innerHTML wipes by
    // design and are otherwise only cleared by unbindBoardFilters() on page teardown, so a
    // dropdown left open before a toggle flip would re-render open on the next project.
    dropdownState.sprint.open = false;
    dropdownState.sprint.scroll = 0;
  } else {
    bindSprintSelector(filters, onChange, sprints, tasks);
  }

  // Type selector — sprint-style dropdown (TPT109)
  bindTypeSelector(filters, onChange);
  if (!isWorkspace(opts)) bindUrgencySelector(filters, onChange);

  // Assignee selector — sprint/type-style dropdown (TPT110)
  bindAssigneeSelector(filters, onChange);

  // Status selector — sprint/type/assignee-style dropdown (TPT108)
  bindStatusSelector(filters, onChange, statuses);

  // Tag typeahead
  const tagInput = document.getElementById('bf-tag-input');
  const tagSuggestions = document.getElementById('bf-tag-suggestions');
  if (tagInput && tagSuggestions) {
    tagInput.addEventListener('input', debounce(() => {
      const val = tagInput.value.trim().toLowerCase();
      if (!val) { tagSuggestions.innerHTML = ''; return; }
      const matches = allTags
        .filter(t => t.toLowerCase().includes(val) && !filters.tags.includes(t))
        .slice(0, 8);
      tagSuggestions.innerHTML = matches.map(t =>
        `<div class="bf-tag-suggestion" data-tag="${escapeHtml(t)}">${escapeHtml(t)}</div>`
      ).join('');
    }, 150));

    tagInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && tagInput.value.trim()) {
        e.preventDefault();
        const tag = tagInput.value.trim();
        if (!filters.tags.includes(tag)) {
          filters.tags = [...filters.tags, tag];
          onChange(filters);
        }
        tagInput.value = '';
        tagSuggestions.innerHTML = '';
      }
    });

    tagSuggestions.addEventListener('click', (e) => {
      const item = e.target.closest('.bf-tag-suggestion');
      if (item) {
        const tag = item.dataset.tag;
        if (!filters.tags.includes(tag)) {
          filters.tags = [...filters.tags, tag];
          onChange(filters);
        }
        tagInput.value = '';
        tagSuggestions.innerHTML = '';
      }
    });
  }

  // Tag chip remove
  bar.querySelectorAll('.bf-tag-chip-x').forEach(x => {
    x.addEventListener('click', () => {
      filters.tags = filters.tags.filter(t => t !== x.dataset.tag);
      onChange(filters);
    });
  });

  // Edit Board (C1183)
  const editBoardBtn = document.getElementById('bf-edit-board-btn');
  if (editBoardBtn && onToggleEditBoard) {
    editBoardBtn.addEventListener('click', () => onToggleEditBoard());
  }

  // Clear all — changedKey 'clear' (TPT25): resets q too, and #bf-search still shows the
  // old text until a full rebuild rewrites its value, so this must not take the q-only
  // (C1524) path — it still triggers board.js's full refreshFilterBar() rebuild the same
  // as 'clear' being any other non-'q' value would. board.js additionally reads 'clear'
  // specifically to forget this project's saved filter prefs (lib/board-filter-prefs.js)
  // instead of re-saving the now-empty selection.
  const clearBtn = document.getElementById('bf-clear');
  if (clearBtn) {
    clearBtn.addEventListener('click', (e) => {
      e.preventDefault();
      Object.assign(filters, { q: '', types: [], statuses: [], assignees: [], tags: [], sprints: [], urgencies: [] });
      if (isWorkspace(opts)) filters.projects = [];
      onChange(filters, 'clear');
    });
  }

  // Chip remove
  const chipsEl = document.getElementById('bf-chips');
  if (chipsEl) {
    chipsEl.addEventListener('click', (e) => {
      const rm = e.target.closest('.bf-chip-remove');
      if (!rm) return;
      const filterType = rm.dataset.filter;
      const value = rm.dataset.value;
      if (filterType === 'q') {
        // No changedKey (C1524) — same reasoning as Clear All above: this mutates
        // filters.q while #bf-search still displays the old text, so #bf-search itself
        // needs a full rebuild, not the q-only chip/Clear-All-only sync path.
        filters.q = '';
      } else if (filterType === 'type') {
        filters.types = filters.types.filter(t => t !== value);
      } else if (filterType === 'urgency') {
        filters.urgencies = (filters.urgencies || []).filter(n => n !== Number(value));
      } else if (filterType === 'assignee') {
        filters.assignees = filters.assignees.filter(a => a !== parseInt(value, 10));
      } else if (filterType === 'status') {
        filters.statuses = filters.statuses.filter(s => s !== value);
      } else if (filterType === 'project') {
        filters.projects = (filters.projects || []).filter(p => p !== parseInt(value, 10));
      } else if (filterType === 'sprint') {
        filters.sprints = filters.sprints.filter(n => n !== parseInt(value, 10));
      }
      onChange(filters);
    });
  }

  return token;
}

// Page teardown: drop the document-level outside-click listener and reset every
// dropdown's persisted UI state so a fresh board mount starts closed.
// With a token (TPT643) it acts only while that token owns the binding — a late teardown
// leaves the listeners and open/scroll flags of the bar now on screen alone. No argument
// keeps the unconditional behavior.
export function unbindBoardFilters(token) {
  if (token !== undefined && token !== filterBindingOwner) return;
  filterBindingOwner = null;
  detachFilterOutsideHandler();
  for (const key of Object.keys(dropdownState)) {
    dropdownState[key].open = false;
    dropdownState[key].scroll = 0;
  }
}
